/**
 * The eight scale-free numbers a face shape is decided from.
 *
 * Every one is a ratio of two lengths measured in the same frame, or an angle, so none of them depends on how far away the camera was, what its focal length is, or how many pixels the face covers. None of them uses the iris: the PD method's assumed 11.7 mm has no part in this, which is why a face shape needs no calibration at all.
 *
 * What they do depend on is the face being seen near-frontally. Yaw foreshortens one side, pitch shortens the apparent length, and strong perspective at very close range bows the outline. Those are refused by the gates rather than corrected here.
 */
import { bandWidth, widthAt, type WidthProfile } from './profile';
import { MENTON_INDEX, SUPERIOR_INDEX, type Outline, type Point } from './oval';

/** The height bands the forehead and jaw widths are averaged over. */
export const FOREHEAD_BAND = { from: 0.12, to: 0.26 } as const;
export const JAW_BAND = { from: 0.7, to: 0.85 } as const;
/**
 * The stretch of face the sides are judged straight or curved over.
 *
 * It stops above the jaw corner on purpose. Carried down to the menton it stopped measuring the sides at all and started measuring how fast the chin tapers, which every face does — an oblong face with parallel sides then read as more curved than a square one. The band is the side, and the side ends at the jaw.
 */
export const SIDE_BAND = { from: 0.15, to: 0.72 } as const;

export interface FaceFeatures {
  /** Pole-to-pole length over the widest chord. Above 1 means longer than wide. */
  lengthToWidth: number;
  /** Mean forehead-band width over the widest chord. */
  foreheadToWidest: number;
  /** Mean jaw-band width over the widest chord. */
  jawToWidest: number;
  /** Jaw band over forehead band. Below 1 means the face narrows downwards. */
  jawToForehead: number;
  /** Where the widest chord sits, 0 at the superior pole and 1 at the menton. */
  widestAt: number;
  /** How far the sides bow away from a straight line across the side band, over the widest chord. Near 0 is a straight side. */
  sideStraightness: number;
  /** The outline's turn across the jaw, degrees. Small is a square corner; near 180 is a continuous curve. */
  jawAngleDeg: number;
  /** How much width survives from the jaw band to just above the menton. Low is a pointed chin. */
  chinRoundness: number;
}

const hypot = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * How far a side bows from the straight line joining its ends.
 *
 * Measured on the profile's own half-widths rather than the raw ring, so it is not disturbed by the uneven spacing of the landmarks, and signed so that a face bulging outwards and one pinching inwards do not cancel.
 */
function straightness(profile: WidthProfile): number {
  const inBand: number[] = [];
  const ts: number[] = [];
  for (let i = 0; i < profile.t.length; i += 1) {
    const ti = profile.t[i]!;
    if (ti >= SIDE_BAND.from && ti <= SIDE_BAND.to) {
      inBand.push((profile.left[i]! + profile.right[i]!) / 2);
      ts.push(ti);
    }
  }
  if (inBand.length < 3) return 0;

  const first = inBand[0]!;
  const last = inBand[inBand.length - 1]!;
  const tFirst = ts[0]!;
  const tLast = ts[ts.length - 1]!;
  let sum = 0;
  for (let i = 0; i < inBand.length; i += 1) {
    const f = (ts[i]! - tFirst) / (tLast - tFirst);
    sum += Math.abs(inBand[i]! - (first + (last - first) * f));
  }
  return sum / inBand.length / (profile.maxWidthPx / 2);
}

/** The stretch of face the jaw corner is looked for in. */
export const JAW_CORNER_BAND = { from: 0.6, to: 0.96 } as const;

/**
 * The sharpest turn the outline makes through the jaw region, degrees. 180 is no corner at all.
 *
 * The corner is found rather than assumed. A fixed trio of landmarks cannot measure it: on a square face the width holds until low down and turns late, on a heart-shaped one it turns early and gradually, so any fixed vertex lands beside the corner on most faces and measures something else. Two earlier attempts did exactly that — one sat in the chin and made round faces read as angular, the next sat above the corner and made square faces read as having no jaw at all.
 *
 * It is measured on the resampled profile, so the ring's uneven spacing cannot influence which turn looks sharpest, and on the mean of the two half-widths, so a slightly asymmetric face does not report the sharper of its two sides.
 */
function jawAngle(profile: WidthProfile): number {
  const idx: number[] = [];
  for (let i = 0; i < profile.t.length; i += 1) {
    const ti = profile.t[i]!;
    if (ti >= JAW_CORNER_BAND.from && ti <= JAW_CORNER_BAND.to) idx.push(i);
  }
  if (idx.length < 3) return 180;

  // The side as a curve of half-width against height, both in pixels so the angle is a real one.
  const point = (i: number): Point => ({
    x: (profile.left[i]! + profile.right[i]!) / 2,
    y: profile.t[i]! * profile.lengthPx,
  });

  let sharpest = 180;
  for (let k = 1; k < idx.length - 1; k += 1) {
    const p = point(idx[k - 1]!);
    const q = point(idx[k]!);
    const r = point(idx[k + 1]!);
    const u = { x: p.x - q.x, y: p.y - q.y };
    const v = { x: r.x - q.x, y: r.y - q.y };
    const lu = Math.hypot(u.x, u.y);
    const lv = Math.hypot(v.x, v.y);
    if (lu === 0 || lv === 0) continue;
    const cos = Math.max(-1, Math.min(1, (u.x * v.x + u.y * v.y) / (lu * lv)));
    sharpest = Math.min(sharpest, (Math.acos(cos) * 180) / Math.PI);
  }
  return sharpest;
}

export function extractFeatures(profile: WidthProfile): FaceFeatures {
  const forehead = bandWidth(profile, FOREHEAD_BAND.from, FOREHEAD_BAND.to);
  const jaw = bandWidth(profile, JAW_BAND.from, JAW_BAND.to);
  const wMax = profile.maxWidthPx;

  return {
    lengthToWidth: profile.lengthPx / wMax,
    foreheadToWidest: forehead / wMax,
    jawToWidest: jaw / wMax,
    jawToForehead: forehead === 0 ? 0 : jaw / forehead,
    widestAt: profile.widestAt,
    sideStraightness: straightness(profile),
    jawAngleDeg: jawAngle(profile),
    chinRoundness: jaw === 0 ? 0 : widthAt(profile, 0.92) / jaw,
  };
}

/** The face's pixel height, for the resolution gate. */
export const faceHeightPx = (outline: Outline): number =>
  hypot(outline[SUPERIOR_INDEX]!, outline[MENTON_INDEX]!);
